import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Download, FileSpreadsheet, FileText, File } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

/**
 * Reusable export dropdown for tables.
 * 
 * Props:
 * - filteredData: array of row objects (currently filtered/visible)
 * - allData: array of row objects (all data)
 * - columns: array of { key, label, format? } defining columns to export
 * - filenamePrefix: string prefix for the downloaded file
 * - title: string title for PDF header
 */
export default function ExportDropdown({ filteredData, allData, columns, filenamePrefix = 'export', title = 'Report' }) {
  const [exporting, setExporting] = useState(false);

  const dateStamp = new Date().toISOString().split('T')[0];

  const formatValue = (row, col) => {
    const raw = row[col.key];
    if (raw === undefined || raw === null) return '';
    if (col.format === 'currency') return typeof raw === 'number' ? raw.toFixed(2) : raw;
    if (col.format === 'percent') return typeof raw === 'number' ? `${(raw * 100).toFixed(2)}%` : raw;
    return String(raw);
  };

  const prepareRows = (data) => data.map(row => columns.map(col => formatValue(row, col)));

  const exportCSV = (data, label) => {
    try {
      setExporting(true);
      const header = columns.map(c => c.label).join(',');
      const rows = prepareRows(data).map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','));
      const csv = [header, ...rows].join('\n');
      const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
      saveAs(blob, `${filenamePrefix}_${label}_${dateStamp}.csv`);
      toast.success(`CSV exported (${data.length} records)`);
    } catch (err) {
      toast.error('CSV export failed');
    } finally {
      setExporting(false);
    }
  };

  const exportExcel = (data, label) => {
    try {
      setExporting(true);
      const wsData = [columns.map(c => c.label), ...prepareRows(data)];
      const ws = XLSX.utils.aoa_to_sheet(wsData);

      // Set column widths
      ws['!cols'] = columns.map(col => ({
        wch: Math.max(col.label.length, 14)
      }));

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Data');
      const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      saveAs(new Blob([buf], { type: 'application/octet-stream' }), `${filenamePrefix}_${label}_${dateStamp}.xlsx`);
      toast.success(`Excel exported (${data.length} records)`);
    } catch (err) {
      toast.error('Excel export failed');
    } finally {
      setExporting(false);
    }
  };

  const exportPDF = (data, label) => {
    try {
      setExporting(true);
      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });

      // Header
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Utmost Financial Inc.', 14, 15);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      doc.text(title, 14, 22);
      doc.setFontSize(9);
      doc.setTextColor(100);
      doc.text(`Generated: ${new Date().toLocaleDateString('en-CA')}  |  ${data.length} records  |  ${label === 'filtered' ? 'Filtered Data' : 'All Data'}`, 14, 28);
      doc.setTextColor(0);

      const head = [columns.map(c => c.label)];
      const body = prepareRows(data);

      autoTable(doc, {
        head,
        body,
        startY: 33,
        styles: { fontSize: 7, cellPadding: 2 },
        headStyles: { fillColor: [30, 41, 59], textColor: 255, fontStyle: 'bold', fontSize: 7 },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        columnStyles: columns.reduce((acc, col, i) => {
          if (col.format === 'currency') acc[i] = { halign: 'right' };
          if (col.format === 'percent') acc[i] = { halign: 'right' };
          return acc;
        }, {}),
        margin: { top: 33, left: 14, right: 14 },
        didDrawPage: (hookData) => {
          // Footer
          doc.setFontSize(8);
          doc.setTextColor(150);
          doc.text(
            `Page ${hookData.pageNumber}`,
            doc.internal.pageSize.width / 2,
            doc.internal.pageSize.height - 8,
            { align: 'center' }
          );
        }
      });

      doc.save(`${filenamePrefix}_${label}_${dateStamp}.pdf`);
      toast.success(`PDF exported (${data.length} records)`);
    } catch (err) {
      toast.error('PDF export failed');
    } finally {
      setExporting(false);
    }
  };

  const hasFiltered = filteredData && filteredData.length > 0;
  const hasAll = allData && allData.length > 0;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" disabled={exporting} data-testid="export-dropdown-btn">
          <Download className="w-4 h-4 mr-2" />
          {exporting ? 'Exporting...' : 'Download'}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        {/* CSV Section */}
        <DropdownMenuLabel className="flex items-center gap-2 text-xs">
          <FileText className="w-3.5 h-3.5" /> CSV
        </DropdownMenuLabel>
        <DropdownMenuItem
          onClick={() => exportCSV(filteredData, 'filtered')}
          disabled={!hasFiltered}
          data-testid="export-csv-filtered"
        >
          Filtered Data ({filteredData?.length || 0} rows)
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => exportCSV(allData, 'all')}
          disabled={!hasAll}
          data-testid="export-csv-all"
        >
          All Data ({allData?.length || 0} rows)
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        {/* Excel Section */}
        <DropdownMenuLabel className="flex items-center gap-2 text-xs">
          <FileSpreadsheet className="w-3.5 h-3.5" /> Excel
        </DropdownMenuLabel>
        <DropdownMenuItem
          onClick={() => exportExcel(filteredData, 'filtered')}
          disabled={!hasFiltered}
          data-testid="export-excel-filtered"
        >
          Filtered Data ({filteredData?.length || 0} rows)
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => exportExcel(allData, 'all')}
          disabled={!hasAll}
          data-testid="export-excel-all"
        >
          All Data ({allData?.length || 0} rows)
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        {/* PDF Section */}
        <DropdownMenuLabel className="flex items-center gap-2 text-xs">
          <File className="w-3.5 h-3.5" /> PDF
        </DropdownMenuLabel>
        <DropdownMenuItem
          onClick={() => exportPDF(filteredData, 'filtered')}
          disabled={!hasFiltered}
          data-testid="export-pdf-filtered"
        >
          Filtered Data ({filteredData?.length || 0} rows)
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => exportPDF(allData, 'all')}
          disabled={!hasAll}
          data-testid="export-pdf-all"
        >
          All Data ({allData?.length || 0} rows)
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
